import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AuthContext } from '../context/AuthContext';
import { FaUserCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false); // Mobile menu toggle
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // User dropdown toggle
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0 });
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null); // Ref for dropdown to handle click outside

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Toggle user dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsOpen(false); // Close mobile menu on logout
    setIsDropdownOpen(false); // Close dropdown on logout
  };

  // Handle header opacity animation based on scroll
  useEffect(() => {
    if (inView) {
      controls.start({ opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } });
    } else {
      controls.start({ opacity: 0.9, transition: { duration: 0.3, ease: 'easeOut' } });
    }
  }, [inView, controls]);

  const headerClassName = `bg-white p-4 flex justify-between items-center z-50 fixed top-0 left-0 w-full shadow-md`;

  const logoVariants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
      rotate: -10,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
        type: 'spring',
        stiffness: 120,
        damping: 12,
      },
    },
  };

  const navItemVariants = {
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut', delay: 0.1 },
    },
    hidden: {
      opacity: 0,
      y: -10,
    },
  };

  const navLinkHover = {
    scale: 1.1,
    color: '#2596be',
    y: -2,
    textShadow: '0px 2px 4px rgba(37, 150, 190, 0.3)',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: 0.4,
      },
    },
  };

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: 'easeIn',
      },
    },
  };

  const glowingButton = `px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white font-bold leading-6 hover:from-cyan-600 hover:to-blue-700`;

  const getLinkClassName = (path) => {
    return location.pathname === path
      ? 'text-[#2596be] font-bold font-openSans text-lg leading-6 border-b-2 border-[#2596be]'
      : 'text-[#2596be] font-bold font-openSans text-lg leading-6';
  };

  return (
    <motion.header
      ref={ref}
      initial="hidden"
      animate={controls}
      className={headerClassName}
    >
      {/* Logo */}
      <motion.div
        variants={logoVariants}
        initial="hidden"
        animate="visible"
        className="flex items-center ml-4 md:ml-6"
      >
        <Link to="/" className="flex items-center">
          <motion.img
            src="/logo.png"
            alt="Logo"
            className="w-16 h-auto mr-2"
          />
          <motion.h1
            className="text-xl text-[#2596be] font-montserrat font-bold uppercase leading-6"
          >
            The AI Union
          </motion.h1>
        </Link>
      </motion.div>

      {/* Desktop Menu */}
      <nav className="hidden md:flex items-center space-x-6 mr-4 mt-3 md:mr-6">
        <motion.div variants={navItemVariants} initial="hidden" animate={controls}>
          <Link to="/" className={getLinkClassName('/')}>
            <motion.span whileHover={navLinkHover}>Home</motion.span>
          </Link>
        </motion.div>
        <motion.div
          variants={navItemVariants}
          initial="hidden"
          animate={controls}
          style={{ transitionDelay: '0.15s' }}
        >
          <Link to="/ai-insights" className={getLinkClassName('/ai-insights')}>
            <motion.span whileHover={navLinkHover}>A.i Insights</motion.span>
          </Link>
        </motion.div>
        <motion.div
          variants={navItemVariants}
          initial="hidden"
          animate={controls}
          style={{ transitionDelay: '0.2s' }}
        >
          <Link to="/ai-hire" className={getLinkClassName('/ai-hire')}>
            <motion.span whileHover={navLinkHover}>A.i Hire</motion.span>
          </Link>
        </motion.div>
        <motion.div
          variants={navItemVariants}
          initial="hidden"
          animate={controls}
          style={{ transitionDelay: '0.25s' }}
        >
          <Link to="/services" className={getLinkClassName('/services')}>
            <motion.span whileHover={navLinkHover}>Services</motion.span>
          </Link>
        </motion.div>
        <motion.div
          variants={navItemVariants}
          initial="hidden"
          animate={controls}
          style={{ transitionDelay: '0.25s' }}
        >
          <Link to="/about" className={getLinkClassName('/about')}>
            <motion.span whileHover={navLinkHover}>About Us</motion.span>
          </Link>
        </motion.div>

        {/* Authentication Links (Desktop View) */}
        <motion.div variants={buttonVariants} initial="hidden" animate="visible">
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="flex items-center space-x-2 text-[#2596be] font-bold font-openSans text-base leading-6 focus:outline-none"
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
              >
                <FaUserCircle className="w-8 h-8 text-[#2596be]" />
                <span>Welcome, {user.name}</span>
                {isDropdownOpen ? (
                  <FaChevronUp className="w-4 h-4" />
                ) : (
                  <FaChevronDown className="w-4 h-4" />
                )}
              </button>
              {isDropdownOpen && (
                <motion.div
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-50 border border-gray-200"
                >
                  <div className="px-4 py-3 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700">
                      Welcome, {user.name}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-[#2596be] hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </motion.div>
              )}
            </div>
          ) : (
            <Link to="/login" className={glowingButton}>
              Join/Login
            </Link>
          )}
        </motion.div>
      </nav>

      {/* Mobile Hamburger Menu */}
      <div className="md:hidden mr-4">
        <button onClick={toggleMenu} className="text-[#2596be] focus:outline-none">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d={isOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
            />
          </svg>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <nav className="md:hidden absolute top-16 left-0 w-full bg-white p-4 flex flex-col space-y-2 shadow-md">
          <Link to="/" className={getLinkClassName('/')}>
            <motion.span whileHover={navLinkHover}>Home</motion.span>
          </Link>
          <Link to="/ai-insights" className={getLinkClassName('/ai-insights')}>
            <motion.span whileHover={navLinkHover}>A.i Insights</motion.span>
          </Link>
          <Link to="/ai-hire" className={getLinkClassName('/ai-hire')}>
            <motion.span whileHover={navLinkHover}>A.i Hire</motion.span>
          </Link>
          <Link to="/services" className={getLinkClassName('/services')}>
            <motion.span whileHover={navLinkHover}>Services</motion.span>
          </Link>
          <Link to="/about" className={getLinkClassName('/about')}>
            <motion.span whileHover={navLinkHover}>About Us</motion.span>
          </Link>

          {/* Authentication Links (Mobile View) */}
          <div>
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="flex items-center space-x-2 text-[#2596be] font-bold font-openSans text-base leading-6 focus:outline-none w-full py-2"
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                >
                  <FaUserCircle className="w-6 h-6 text-[#2596be]" />
                  <span>Welcome, {user.name}</span>
                  {isDropdownOpen ? (
                    <FaChevronUp className="w-4 h-4" />
                  ) : (
                    <FaChevronDown className="w-4 h-4" />
                  )}
                </button>
                {isDropdownOpen && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="mt-2 w-full bg-white rounded-lg shadow-lg z-50 border border-gray-200"
                  >
                    <div className="px-4 py-3 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-700">
                        Welcome, {user.name}
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-[#2596be] hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className={`${glowingButton} bg-blue-500 hover:bg-blue-600 w-full`}
              >
                Join/Login
              </Link>
            )}
          </div>
        </nav>
      )}
    </motion.header>
  );
};

export default Header;